import type { Stripe } from "@stripe/stripe-js";
import { SERVER_URL } from "../../constants/global";

export type SaveCheckoutCardParams = {
  stripe: Stripe;
  token: string;
  paymentMethodId: string;
  cardholderName: string;
  cardBrand: string;
  cardLast4: string;
  bankAccountId: number;
  pharmacyId: number;
  userId: number;
  oldAccountId?: string | null;
  updateBankAccount: (options: {
    variables: { data: Record<string, unknown>; where: { id: number } };
  }) => Promise<unknown>;
};

export async function saveCheckoutCardToBankAccount({
  stripe,
  token,
  paymentMethodId,
  cardholderName,
  cardBrand,
  cardLast4,
  bankAccountId,
  pharmacyId,
  userId,
  oldAccountId,
  updateBankAccount,
}: SaveCheckoutCardParams): Promise<void> {
  const setupResponse = await fetch(`${SERVER_URL}/stripe/setup-intent?type=card`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  const setupData = await setupResponse.json().catch(() => ({}));

  if (!setupResponse.ok) {
    const apiMessage =
      typeof setupData?.message === "string"
        ? setupData.message
        : Array.isArray(setupData?.message)
          ? setupData.message.join(", ")
          : null;
    throw new Error(apiMessage || "Failed to initialize card setup. Please try again.");
  }

  // API returns snake_case (same as Bank Account page).
  const clientSecret = setupData?.client_secret ?? setupData?.clientSecret;
  if (!clientSecret) {
    throw new Error("Failed to initialize card setup. Please try again.");
  }

  const { setupIntent, error: setupError } = await stripe.confirmCardSetup(clientSecret, {
    payment_method: paymentMethodId,
  });

  if (setupError) {
    throw new Error(setupError.message || "Card setup failed.");
  }

  if (!setupIntent || setupIntent.status !== "succeeded") {
    throw new Error("Card setup did not complete. Please try again.");
  }

  const displayBrand = cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1);

  await updateBankAccount({
    variables: {
      data: {
        Pharmacy: { connect: { id: pharmacyId } },
        User: { connect: { id: userId } },
        accountHolderName: { set: cardholderName },
        accountId: { set: paymentMethodId },
        bankName: { set: displayBrand },
        country: { set: "United States" },
        currency: { set: "USD" },
        last4: { set: cardLast4 },
        object: { set: "card" },
        routingNumber: { set: null },
        status: { set: "VERIFIED" },
        token: { set: setupIntent.id },
        hostedVerificationUrl: { set: null },
      },
      where: { id: bankAccountId },
    },
  });

  if (oldAccountId && oldAccountId !== paymentMethodId) {
    fetch(`${SERVER_URL}/stripe/payment-method/${oldAccountId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {
      /* best-effort detach of replaced method */
    });
  }
}
